import { makeObservable, observable, action, runInAction } from "mobx";
import axios, { GenericAbortSignal } from "axios";
import { api } from "../../api/endpoints";
import AppStore from "../../app.store";
import { CashSession, CashSessionsFilterModel, ReplenishmentCandidate } from "./cash_sessions.model";
import { toast } from "react-toastify";
import { BaseStore } from "../../core/BaseStore";
import { cleanParams } from "../../core/utils";

class CashSessionsStore extends BaseStore {
    public currentSession: CashSession | null = null;
    public sessions: CashSession[] = [];
    public replenishmentCandidates: ReplenishmentCandidate[] = [];
    public isReplenishmentLoading = false;
    public sessionsFilter: CashSessionsFilterModel = {
        page: 1,
        page_limit: 25,
        pages: 0
    };

    constructor() {
        super();
        makeObservable(this, {
            currentSession: observable,
            sessions: observable,
            replenishmentCandidates: observable,
            isReplenishmentLoading: observable,
            sessionsFilter: observable,
            setSessionsFilter: action,
            clearSessionsFilter: action,
        });
    }

    public fetchCurrentSession = async (businessUnitId: number, signal?: GenericAbortSignal) => {
        if (!businessUnitId) return;
        try {
            const response = await axios.get(api.cash_session_current, {
                params: { business_unit_id: businessUnitId },
                signal
            });
            runInAction(() => {
                this.currentSession = response.data.session;
            });
        } catch (error: any) {
            if (error.response?.status === 404) {
                runInAction(() => {
                    this.currentSession = null;
                });
            } else if (axios.isCancel(error)) {
                // Ignore cancel
            } else {
                console.error("Failed to fetch current cash session", error);
            }
        }
    };

    public fetchSessions = async (signal?: GenericAbortSignal) => {
        this.setLoading(true);
        try {
            const params = cleanParams({ ...this.sessionsFilter });
            // Remove pages from params as it's not needed by API
            delete (params as any).pages;

            const response = await axios.get(api.cash_sessions, {
                params,
                signal
            });
            runInAction(() => {
                this.sessions = response.data.sessions;
                this.sessionsFilter.pages = response.data.pages;
            });
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Failed to fetch cash sessions", error);
            }
        } finally {
            this.setLoading(false);
        }
    };

    public openCashSession = async (data: { business_unit_id: number; opening_cash: number; note?: string }) => {
        try {
            const response = await axios.post(api.cash_session_open, data);
            runInAction(() => {
                this.currentSession = response.data.session;
            });
            toast.success("Кассовая сессия открыта");
            void this.fetchSessions();
            return true;
        } catch (error: any) {
            if (error.response?.status === 409) {
                toast.warning("У вас уже есть открытая кассовая сессия");
                // Можно попробовать подтянуть текущую сессию
                if (data.business_unit_id) {
                    void this.fetchCurrentSession(data.business_unit_id);
                }
            } else {
                const reason = error.response?.data?.reason || "Ошибка при открытии кассовой сессии";
                toast.error(reason);
            }
            return false;
        }
    };

    public closeCashSession = async (id: number, data: { closing_cash: number; note?: string }) => {
        try {
            const response = await axios.post(`${api.cash_sessions}/${id}/close`, data);
            runInAction(() => {
                if (this.currentSession?.id === id) {
                    this.currentSession = null;
                }
            });
            toast.success("Кассовая сессия закрыта");
            void this.fetchSessions();
            return true;
        } catch (error: any) {
            const reason = error.response?.data?.reason || "Ошибка при закрытии кассовой сессии";
            toast.error(reason);
            return false;
        }
    };

    public fetchReplenishmentCandidates = async (businessUnitId: number, minNeed?: number, signal?: GenericAbortSignal) => {
        this.isReplenishmentLoading = true;
        try {
            const response = await axios.get(api.replenishment_candidates, {
                params: { business_unit_id: businessUnitId, min_need: minNeed },
                signal
            });
            runInAction(() => {
                this.replenishmentCandidates = response.data.candidates;
            });
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Failed to fetch replenishment candidates", error);
            }
        } finally {
            runInAction(() => {
                this.isReplenishmentLoading = false;
            });
        }
    };

    public setSessionsFilter = (filter: Partial<CashSessionsFilterModel>) => {
        const isUndefined = (value: any) => typeof value === 'undefined';
        const hasKey = (obj: any, key: string) => key in obj;
        this.sessionsFilter = { 
            ...this.sessionsFilter, 
            ...filter,
            business_unit_id: isUndefined(filter.business_unit_id) ? this.sessionsFilter.business_unit_id : filter.business_unit_id,
            user_id: isUndefined(filter.user_id) ? this.sessionsFilter.user_id : filter.user_id,
            status: isUndefined(filter.status) ? this.sessionsFilter.status : filter.status,
            date_from: hasKey(filter, 'date_from') ? filter.date_from : this.sessionsFilter.date_from,
            date_to: hasKey(filter, 'date_to') ? filter.date_to : this.sessionsFilter.date_to,
            page: isUndefined(filter.page) ? this.sessionsFilter.page : filter.page,
            page_limit: isUndefined(filter.page_limit) ? this.sessionsFilter.page_limit : filter.page_limit
        };
        if (filter.page_limit || !isUndefined(filter.business_unit_id) || !isUndefined(filter.user_id) || !isUndefined(filter.status) || hasKey(filter, 'date_from')) {
            this.sessionsFilter.page = 1;
        }
        void this.fetchSessions();
    };

    public clearSessionsFilter = () => {
        this.sessionsFilter = {
            page: 1,
            page_limit: 25,
            pages: 0
        };
    };
}

export default new CashSessionsStore();
